import cx from 'classnames'
import NextImage from 'next/image'
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react'

import { type SanityFrameBuilderTextureImage } from '../../data/sanity/queries/types/texture'
import { getBasicImageLoader, getSanityImageUrl } from '../../lib/image'

interface ThumbnailProps {
  index: number
  image: SanityFrameBuilderTextureImage
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  className?: string
}

interface ThumbnailsProps {
  images: SanityFrameBuilderTextureImage[]
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  className?: string
}

const Thumbnail = ({
  index,
  image,
  setSelectedIndex,
  setIsModalOpen,
  className,
}: ThumbnailProps) => {
  const imageUrl = useMemo(
    () =>
      getSanityImageUrl(image.image, {
        width: 75,
        height: 75,
      }),
    [image.image],
  )

  const handleClick = useCallback(() => {
    setSelectedIndex(index)
    setIsModalOpen(true)
  }, [index, setSelectedIndex, setIsModalOpen])

  if (!imageUrl) {
    return
  }

  return (
    <div className={cx('border border-gray-400 cursor-pointer', className)}>
      <NextImage
        src={imageUrl}
        width={75}
        height={75}
        alt={image.alternativeText}
        loader={getBasicImageLoader}
        unoptimized
        onClick={handleClick}
        className="block object-cover w-[42px] h-[42px] sm:w-[60px] sm:h-[60px] md:w-[75px] md:h-[75px]"
      />
    </div>
  )
}

const Thumbnails = ({
  images,
  setSelectedIndex,
  setIsModalOpen,
  className,
}: ThumbnailsProps) => {
  return (
    <div
      className={cx(
        'flex justify-center items-start w-full space-x-2',
        className,
      )}
    >
      {images.map((image, index) => (
        <Thumbnail
          key={index}
          index={index}
          image={image}
          setSelectedIndex={setSelectedIndex}
          setIsModalOpen={setIsModalOpen}
        />
      ))}
    </div>
  )
}

export default Thumbnails
